// axiosFactory.ts
import axios, { AxiosHeaders, AxiosInstance } from 'axios';
import { isTokenExpired, setTokens } from './../utils/jwtUtils';
import Cookies from 'js-cookie';
import { environment } from 'src/environments/environment';

const refreshAuthToken = async (config: any): Promise<string | null> => {
  try {
    const refreshToken = Cookies.get('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const headers = config.headers as AxiosHeaders;
    headers['Content-Type'] = 'application/json';
    headers['refresh-token'] = refreshToken;

    // Send a request to refresh the token
    const response = await axios.post<any>(
      environment.REACT_APP_CLIENT_BASE_URL + '/api/client/refresh/token',
      {}, // Empty body, as refresh request is header-based
      { headers }
    );

    const authToken = response.data.data.token;
    const newRefreshToken = response.data.data['refresh-token'];

    setTokens(authToken, newRefreshToken);

    return authToken;
  } catch (error) {
    console.error('Failed to refresh token', error);
    return null;
  }
};

const createApiClient = (baseUrl: string): AxiosInstance => {
  const apiClient: AxiosInstance = axios.create({
    baseURL: baseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  let isRefreshing = false;
  let pendingRequests: ((token: string) => void)[] = [];

  const processQueue = (error: any, token: string | null) => {
    pendingRequests.forEach((callback) => {
      if (token) {
        callback(token);
      } else {
        callback('');
      }
    });
    pendingRequests = [];
  };

  apiClient.interceptors.request.use(
    async (config) => {
      const token = Cookies.get('authToken');
      const refreshToken = Cookies.get('refreshToken');

      if (!refreshToken || isTokenExpired(refreshToken)) {
        console.error('Refresh Token is expired');
        return Promise.reject(new Error('Refresh token expired'));
      }

      if (token && !isTokenExpired(token)) {
        config.headers.Authorization = `Bearer ${token}`;
        config.headers.source = `benefitportal`;

        return config;
      }

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAuthToken(config);
          isRefreshing = false;
          processQueue(null, newToken);
          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
            config.headers.source = `benefitportal`;
          }
        } catch (err) {
          isRefreshing = false;
          processQueue(err, null);
          return Promise.reject(err);
        }
      } else {
        return new Promise((resolve, reject) => {
          pendingRequests.push((token: string) => {
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
              config.headers.source = `benefitportal`;
              resolve(config);
            } else {
              reject(new Error('Token refresh failed'));
            }
          });
        });
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && error.response.status === 401) {
        Cookies.remove('authToken');
        Cookies.remove('refreshToken');
      }
      return Promise.reject(error);
    }
  );

  return apiClient;
};

export default createApiClient;
